<template>
  <div v-if="chartData" class="pressure-traverse-chart" :class="{ isStatic }">
    <ScatterChart
      v-bind="$attrs"
      :draw-x-axis="
        !chartData.pressureTraverseDataSet.points.every(p => p.x === 0)
      "
      :draw-y-axis="
        !chartData.pressureTraverseDataSet.points.every(p => p.y === 0)
      "
      draw-curve
      :title="title"
      :data-sets="[chartData.pressureTraverseDataSet]"
      absolute-scale
      :tick-frequency-x="ddvTickFrequencies?.x"
      :tick-frequency-y="ddvTickFrequencies?.y"
    >
      <template
        v-if="
          chartData.pressureTraverseDataSet.points.every(({ x }) => x === 0)
        "
        #background="{ domState, margins }"
      >
        <rect
          v-if="domState.plotWidth > 0 && domState.plotHeight > 0"
          class="plotBg"
          :x="margins.left"
          :y="margins.top"
          :width="domState.plotWidth"
          :height="domState.plotHeight"
        />
        <text
          x="50%"
          y="50%"
          fill="var(--plotColor0)"
          text-anchor="middle"
          dominant-baseline="middle"
          >No Data</text
        >
      </template>
      <template
        v-if="
          chartData.pressureTraverseDataSet.points.every(({ x }) => x === 0)
        "
        #crosshairs
      >
        Return none
      </template>
      <template #perSeries="{ dataSeriesNumber, nearestPoint, screenPoints }">
        <LinePlot
          v-if="screenPoints.length"
          :screen-points="screenPoints"
          :data-series-number="dataSeriesNumber"
          curve-interpolator="straight"
        />
        <NearestPoint
          v-if="nearestPoint?.node"
          :data-series-number="dataSeriesNumber"
          :nearest-point="nearestPoint"
        />
      </template>
    </ScatterChart>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import { ScatterChart, LinePlot, NearestPoint } from "@incytestudios/ddv";
import type { Scenario, WellProperties } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { usePressureTraverseChartData } from "@/libs/charting/pressureTraverse/usePressureTraverseChartData";
import {
  sizingKey,
  scenarioCalculationsKey,
  scenarioKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { injectStrict } from "@/utils/utils";

const props = defineProps<{
  title?: string;
  isStatic?: boolean;
  isHorizontal?: boolean;
  scenario?: Scenario | null;
  scenarioCalculations?: ScenarioCalculations;
  wellProperties?: WellProperties;
}>();
const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

const _scenario = props.scenario ?? injectStrict(scenarioKey);
const _sizing = injectStrict(sizingKey, null);
const { getPressureUnit, getLargeLengthUnit } = useScenarioUnits(
  _scenario,
  _sizing
);
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const _calcs =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);

const chartData = computed(() =>
  usePressureTraverseChartData(
    _calcs,
    targetPressureUnit.value,
    targetLargeLengthUnit.value
  )
);
</script>

<style lang="scss" scoped>
.pressure-traverse-chart {
  width: 95%;

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }
}
</style>
